import type { FeatureGridProps } from "../../../types/content.types";
import { ArrowRightIcon } from "@heroicons/react/24/solid";

const sizeStyles = {
    sm: {
        section: "py-10",
        grid: "gap-4",
        card: "rounded-xl",
        padding: "p-4",
        title: "text-lg",
        desc: "text-sm",
    },
    md: {
        section: "py-16",
        grid: "gap-6",
        card: "rounded-2xl",
        padding: "p-6",
        title: "text-xl",
        desc: "text-base",
    },
};

export default function FeatureGrid({
                                        id,
                                        title,
                                        items,
                                        size = "sm",
                                        dark = false,
                                        showAccent = false,
                                    }: FeatureGridProps) {
    const styles = sizeStyles[size];

    return (
        <section
            id={id}
            className={`
        ${styles.section}
        ${dark ? "text-white" : "text-neutral-900"}
      `}
        >
            <div className="mx-auto max-w-7xl px-4">
                {title && (
                    <header className="mb-10 max-w-2xl">
                        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
                    </header>
                )}

                <div
                    className={`
            grid
            grid-flow-col
            auto-cols-[85%]
            sm:auto-cols-[380px]
            gap-4
            overflow-x-auto
            overscroll-x-contain
            scroll-smooth
            snap-x
            snap-mandatory
            md:grid-flow-row
            md:auto-cols-auto
            md:grid-cols-3
            md:overflow-visible
            ${styles.grid}
          `}
                >
                    {items.map((item, idx) => {
                        const tileShowAccent = item.showAccent ?? showAccent;

                        const headingColor = item.headingColor ?? (dark ? "#f5f5f5" : "#171717");
                        const accentColor = item.accentColor ?? (dark ? "#3b82f6" : "#2563eb");

                        // CTA text color inside accent bar
                        const accentTextColor = dark ? "#000000" : "#ffffff";

                        return (
                            <a
                                key={idx}
                                href={item.cta?.href || "#"}
                                className={`
                  snap-start
                  shrink-0
                  group
                  relative
                  block
                  overflow-hidden
                  border
                  ${dark ? "border-neutral-800 bg-neutral-900" : "border-neutral-200 bg-neutral-50"}
                  ${styles.card}
                  transition
                  hover:-translate-y-0.5
                  hover:shadow-lg
                `}
                            >
                                {item.image && (
                                    <div className="relative aspect-[16/9] overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                    </div>
                                )}

                                <div className={styles.padding}>
                                    <h3
                                        className={`
                      ${styles.title}
                      mb-2
                      font-medium
                      flex
                      items-center
                      gap-2
                    `}
                                        style={{ color: headingColor }}
                                    >
                                        {item.icon && (
                                            <span className="inline-flex h-4 w-4 shrink-0">
                        {item.icon}
                      </span>
                                        )}
                                        {item.title}
                                    </h3>

                                    <p
                                        className={`
                      ${styles.desc}
                      leading-relaxed
                      ${dark ? "text-neutral-400" : "text-neutral-600"}
                    `}
                                    >
                                        {item.description}
                                    </p>
                                </div>

                                {/* Accent CTA Teaser Bar */}
                                {tileShowAccent && item.cta && (
                                    <span
                                        className="
                      pointer-events-none
                      absolute
                      bottom-0
                      left-0
                      flex
                      w-full
                      items-center
                      justify-between
                      px-4
                      overflow-hidden
                      h-1
                      group-hover:h-12
                      transition-all
                      duration-300
                    "
                                        style={{
                                            backgroundColor: accentColor,
                                            color: accentTextColor,
                                        }}
                                    >
                    <span
                        className="
                        translate-y-6
                        opacity-0
                        transition-all
                        duration-300
                        group-hover:translate-y-0
                        group-hover:opacity-100
                      "
                    >
                      {item.cta.label}
                    </span>

                    <ArrowRightIcon
                        aria-hidden
                        className="
                        h-4 w-4
                        translate-y-6
                        opacity-0
                        transition-all
                        duration-300
                        ease-out
                        group-hover:translate-y-0
                        group-hover:opacity-100
                        group-hover:translate-x-1
                      "
                    />
                  </span>
                                )}
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
